import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';

class InstConfigurationKey {
}
InstConfigurationKey.App = 'app';
InstConfigurationKey.Core = 'app.core';
InstConfigurationKey.Features = 'app.features';
InstConfigurationKey.Shared = 'app.shared';
InstConfigurationKey.ThemeColorPalette = 'theme.colorPalette';

class InstanceDefinition {
    constructor({ configurations, options, features }) {
        this.configurations = configurations !== null && configurations !== void 0 ? configurations : [];
        this.options = options !== null && options !== void 0 ? options : [];
        this.features = features !== null && features !== void 0 ? features : [];
        if (!this.validate()) {
            throw new Error('Invalid EnvironmentDefinition');
        }
    }
    getConfigurationsMap(configurationsPrefix = undefined) {
        var _a;
        if (!!configurationsPrefix) {
            configurationsPrefix += '.';
        }
        const prefixDepth = (_a = ((configurationsPrefix === null || configurationsPrefix === void 0 ? void 0 : configurationsPrefix.match(/\./g)) || []).length) !== null && _a !== void 0 ? _a : 0;
        const result = {};
        this.configurations.filter(s => !!configurationsPrefix ? s.key.startsWith(configurationsPrefix) : true).forEach(e => {
            const nodes = e.key.split('.').slice(prefixDepth);
            const valueNode = nodes.pop();
            let node = result;
            nodes.forEach(function (key) {
                node = node[key] = node[key] || {};
            });
            if (valueNode) {
                node[valueNode] = e.value;
            }
        });
        return result;
    }
    getOptionsMap(optionsPrefix = undefined) {
        var _a;
        if (!!optionsPrefix) {
            optionsPrefix += '.';
        }
        const prefixDepth = (_a = ((optionsPrefix === null || optionsPrefix === void 0 ? void 0 : optionsPrefix.match(/\./g)) || []).length) !== null && _a !== void 0 ? _a : 0;
        const result = {};
        this.options.filter(s => !!optionsPrefix ? s.key.startsWith(optionsPrefix) : true).forEach(e => {
            const nodes = e.key.split('.').slice(prefixDepth);
            const valueNode = nodes.pop();
            let node = result;
            nodes.forEach(function (key) {
                node = node[key] = node[key] || {};
            });
            if (valueNode) {
                node[valueNode] = e.value;
            }
        });
        return result;
    }
    validate() {
        try {
            this.getConfigurationsMap();
        }
        catch (_a) {
            throw new Error(`
      Namespaced configurations need to be separated by nodes without values.
      Having 'app.logging.logLevel=debug' and 'app.logging.LogLevel.production=error' would cause a collision.
      A correct example would be 'app.logging.logLevel.default=debug' and 'app.logging.LogLevel.production=error'`);
        }
        try {
            this.getOptionsMap();
        }
        catch (_b) {
            throw new Error(`
      Namespaced options need to be separated by nodes without values.
      Having 'app.logging.logLevel=debug' and 'app.logging.LogLevel.production=error' would cause a collision.
      A correct example would be 'app.logging.logLevel.default=debug' and 'app.logging.LogLevel.production=error'`);
        }
        return !!this.configurations && !!this.options && !!this.features;
    }
    tryValidate() {
        try {
            return this.validate();
        }
        catch (_a) {
            return false;
        }
    }
}

class InstFeature {
    constructor({ key, state }) {
        this.key = key;
        this.state = state;
    }
}

class InstOptionKey {
}
InstOptionKey.App = 'app';
InstOptionKey.Core = 'app.core';
InstOptionKey.Features = 'app.features';
InstOptionKey.Shared = 'app.shared';

const CSH_INSTANCE_DEFINITION_URL = new InjectionToken('CSH_INSTANCE_DEFINITION_URL');
class InstanceInitializerService {
    constructor(httpClient, definitionUrl) {
        this.httpClient = httpClient;
        this.isLoaded = false;
        this.noCacheHeader = {
            headers: new HttpHeaders({
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                Expires: '0'
            })
        };
        this.definitionUrl = definitionUrl ? definitionUrl : 'configuration.json';
    }
    initialize() {
        return this.httpClient.get(this.definitionUrl, this.noCacheHeader)
            .pipe(map(response => new InstanceDefinition(response)))
            .pipe(tap((instanceDefinition) => {
            this.inst = instanceDefinition;
            this.isLoaded = true;
        }));
    }
}
InstanceInitializerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: InstanceInitializerService, deps: [{ token: i1.HttpClient }, { token: CSH_INSTANCE_DEFINITION_URL, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
InstanceInitializerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: InstanceInitializerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: InstanceInitializerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CSH_INSTANCE_DEFINITION_URL]
                }] }]; } });

const CSH_INSTANCE_DEFINITION = new InjectionToken('CSH_INSTANCE_DEFINITION');
class InstanceService {
    /**
     *
     * @param definition
     */
    constructor(definition) {
        this.definition = definition;
    }
    /**
     *
     * @param key
     * @return {InstOption | undefined}
     */
    getConfiguration(key) {
        return this.definition.configurations.find((c) => c.key === key);
    }
    /**
     *
     * @param prefix
     * @return {Record<string, unknown>}
     */
    getConfigurationsMap(prefix = undefined) {
        return this.definition.getConfigurationsMap(prefix);
    }
    /**
     *
     * @return {InstOption[]}
     */
    allConfigurations() {
        return this.definition.configurations;
    }
    /**
     *
     * @param key
     * @return {InstFeature | undefined}
     */
    getFeature(key) {
        return this.definition.features.find(f => f.key === key);
    }
    /**
     *
     * @return {InstFeature[]}
     */
    allFeatures() {
        return this.definition.features;
    }
    /**
     *
     * @param key
     * @return {InstConfiguration | undefined}
     */
    getOption(key) {
        return this.definition.options.find((c) => c.key === key);
    }
    /**
     *
     * @param prefix
     * @return {Record<string, unknown>}
     */
    getOptionsMap(prefix = undefined) {
        return this.definition.getOptionsMap(prefix);
    }
    /**
     *
     * @return {InstConfiguration[]}
     */
    allOptions() {
        return this.definition.options;
    }
}
InstanceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: InstanceService, deps: [{ token: CSH_INSTANCE_DEFINITION }], target: i0.ɵɵFactoryTarget.Injectable });
InstanceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: InstanceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.1.3", ngImport: i0, type: InstanceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: InstanceDefinition, decorators: [{
                    type: Inject,
                    args: [CSH_INSTANCE_DEFINITION]
                }] }]; } });

const appInstanceInitializer = (instanceInitializerService) => {
    return () => instanceInitializerService.initialize().toPromise();
};

const CSH_INSTANCE_ROOT_COMPONENT = new InjectionToken('CSH_INSTANCE_ROOT_COMPONENT');
/**
 * Returns the first found matching component. If the application or component is included in
 * the page more than once, the proper root component being returned cannot be guaranteed.
 *
 * @param selector
 * @return {any}
 */
function getRootComponentBySelector(selector) {
    const component = document.body.querySelector(selector);
    if (component) {
        return component;
    }
    else {
        return undefined;
    }
}
/**
 * Can only be utilized after `APP_BOOTSTRAP_LISTENER`. Unfortunately, this can often create
 * timing issues for dependencies further in the application that are trying to use values which
 * haven't yet been loaded. However, this is the most accurate method to get the correct attributes
 * for configuration.
 *
 * @param appId
 * @return {HTMLElement | undefined}
 */
const getRootComponentByAppId = (appId) => {
    var _a;
    const snapshot = document.evaluate(`//*/attribute::*[starts-with(name(), "_nghost-${appId}-")]`, document, null, XPathResult.ORDERED_NODE_SNAPSHOT_TYPE, null);
    if (snapshot.snapshotLength < 1) {
        console.error('Unable to find the root element.');
        return undefined;
    }
    else if (snapshot.snapshotLength > 1) {
        console.warn('Multiple root elements found. Taking the first occurrence.');
    }
    // We can assume the Attr type because of our evaluate query pattern explicitly looks for an attribute
    const attr = snapshot.snapshotItem(0);
    return (_a = attr === null || attr === void 0 ? void 0 : attr.ownerElement) !== null && _a !== void 0 ? _a : undefined;
};
/**
 * Get the configuration url attribute from the root component.
 *
 * @param component
 * @return {string | undefined}
 */
const getInstanceConfigurationUrl = (component) => {
    var _a;
    return (_a = component.getAttribute('configurationurl')) !== null && _a !== void 0 ? _a : undefined;
};

/**
 *
 * @type {{provide: InjectionToken<string>, useFactory: (component: HTMLElement) => (string | undefined), deps: InjectionToken<HTMLElement>[]}}
 */
const provideRootComponentConfigurationUrlAsInstanceDefinitionUrl = {
    provide: CSH_INSTANCE_DEFINITION_URL,
    useFactory: getInstanceConfigurationUrl,
    deps: [CSH_INSTANCE_ROOT_COMPONENT]
};

/**
 * Generated bundle index. Do not edit.
 */

export { CSH_INSTANCE_DEFINITION, CSH_INSTANCE_DEFINITION_URL, CSH_INSTANCE_ROOT_COMPONENT, InstConfigurationKey, InstFeature, InstOptionKey, InstanceDefinition, InstanceInitializerService, InstanceService, appInstanceInitializer, getInstanceConfigurationUrl, getRootComponentByAppId, getRootComponentBySelector, provideRootComponentConfigurationUrlAsInstanceDefinitionUrl };
//# sourceMappingURL=commonspirit-ngx-instance.js.map
